import React, { useEffect, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Container, Grid, Typography, Box, Button, Paper, Divider, 
    TextField, Avatar, Stack, Chip, useTheme, alpha, Alert 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMapMarkerAlt, faReceipt, faCheckCircle, faWallet, 
    faStore, faExclamationTriangle, faArrowRight 
} from '@fortawesome/free-solid-svg-icons';

// Contextos y Controladores
import UserContext from '../../../navigation/context/userContext'; 
import { getMyCart } from '../../../controllers/commerce/CartController';
import { createMarketplaceOrders } from '../../../controllers/commerce/CheckoutController';
import { payTransactionGroup } from '../../../controllers/customer/ClientPaymentController'; // Función para pagar con saldo

// Componentes
import CustomBreadcrumbs from '../../../components/breadcrumbs';
import QUDProgress from '../../../components/progress'; 
import ResponseComponent from '../../../components/responseComponent'; 
import MultiOrderPaymentContainer from '../../../components/payments/MultiOrderPaymentContainer'; 

export default function CheckoutPage() {
    const theme = useTheme();
    const history = useHistory();
    const { userData, setUserData } = useContext(UserContext); // setUserData para actualizar saldo visualmente si baja

    // --- ESTADOS ---
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [cartData, setCartData] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0.00); 

    // Estado del Resultado (Órdenes Generadas)
    const [createdOrderData, setCreatedOrderData] = useState(null); 

    // Formulario
    const [formData, setFormData] = useState({
        address: '', city: '', zip: '', notes: ''
    });

    const [feedback, setFeedback] = useState({ show: false, success: false, title: '', message: '' });

    // 1. Cargar Datos Iniciales
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const res = await getMyCart();
        
        if (res.success && res.data && res.data.items.length > 0) {
            setCartData(res.data);
            
            // Cargar datos de usuario (Dirección y Saldo)
            if (userData?.user) {
                setFormData(prev => ({ ...prev, address: userData.user.address || '' }));
                // Idealmente traer saldo fresco del API, aquí usamos el del contexto/mock
                setWalletBalance(parseFloat(userData.user.wallet_balance || 0)); 
            }
        } else {
            history.replace('/'); 
        }
        setLoading(false);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // ----------------------------------------------------------------------
    // FASE 1: GENERAR PEDIDOS
    // ----------------------------------------------------------------------
    const handleGenerateOrders = async () => {
        if (!formData.address) {
            setFeedback({ show: true, success: false, title: 'Faltan Datos', message: 'Ingresa una dirección de entrega.' });
            return;
        }

        setProcessing(true);

        // Creamos las órdenes en BD
        const response = await createMarketplaceOrders({
            shipping_address: formData,
            cart_uuid: cartData.uuid
        });

        console.log('HANDLE GENERATE ORDERS ===> ',response);

        if (response.success) {
            // Guardamos la respuesta: { orders: [...], total_to_pay: 150.00, reference: 'UUID-GRUPO' }
            setCreatedOrderData(response.data); 
            window.scrollTo(0,0);
        } else {
            setFeedback({ show: true, success: false, title: 'Error', message: response.message });
        }
        setProcessing(false);
    };

    // ----------------------------------------------------------------------
    // FASE 2: PAGO (Saldo Puro)
    // ----------------------------------------------------------------------
    const handlePayWithWallet = async () => {
        if(!window.confirm("¿Confirmar pago con tu saldo disponible?")) return;
        
        //setProcessing(true);
        // Usamos la referencia de grupo que nos dio el backend al crear las órdenes
        //const response = await payTransactionGroup(createdOrderData.reference);

        /*if (response.success) {
            // Actualizar contexto (opcional)
            // setUserData(...) 
            history.push('/orders/success'); // Redirigir a pantalla de éxito final
        } else {
            setFeedback({ show: true, success: false, title: 'Error', message: response.message });
        }
        setProcessing(false);*/
    };

    // Callback cuando el componente de recarga (MultiOrderPaymentContainer) termina con éxito
    const handleRechargeSuccess = (data) => {
        if(data) {
             history.push('/orders/marketplace');
        }
    };


    // ----------------------------------------------------------------------
    // RENDERIZADO
    // ----------------------------------------------------------------------
    
    if (loading) return <QUDProgress open={true} />;

    // === VISTA B: PEDIDOS CREADOS (PANTALLA DE PAGO) ===
    if (createdOrderData) {
        
        const totalToPay = parseFloat(createdOrderData.total_to_pay);
        const missingAmount = totalToPay - walletBalance;
        const needsRecharge = missingAmount > 0;

        return (
            <Container maxWidth="md" sx={{ pt: 6, pb: 8 }}>
                <Paper elevation={0} sx={{ p: {xs: 2, md: 4}, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                    
                    {/* Header de Éxito Parcial */}
                    <Box textAlign="center" mb={4}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.lighter', color: 'primary.main', mx: 'auto', mb: 2 }}>
                            <FontAwesomeIcon icon={faReceipt} size="lg" />
                        </Avatar>
                        <Typography variant="h5" fontWeight="bold">Pedidos Generados</Typography>
                    </Box>

                    {/* Resumen de Estado Financiero */}
                    <Box sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: 2, mb: 4 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="text.secondary">Total a Pagar</Typography>
                                <Typography variant="h4" fontWeight="bold" color="text.primary">
                                    ${totalToPay.toFixed(2)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ textAlign: {sm: 'right'} }}>
                                <Typography variant="body2" color="text.secondary">Tu Saldo Disponible</Typography>
                                <Box display="flex" alignItems="center" justifyContent={{xs:'flex-start', sm:'flex-end'}} gap={1}>
                                    <FontAwesomeIcon icon={faWallet} color="#555"/>
                                    <Typography variant="h6" fontWeight="bold">
                                        ${walletBalance.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {needsRecharge ? (
                            <Alert severity="warning" icon={<FontAwesomeIcon icon={faExclamationTriangle}/>}>
                                <strong>Saldo Insuficiente.</strong> Necesitas recargar <strong>${missingAmount.toFixed(2)}</strong> para completar el pago.
                            </Alert>
                        ) : (
                            <Alert severity="success" icon={<FontAwesomeIcon icon={faCheckCircle}/>}>
                                <strong>¡Todo listo!</strong> Tienes saldo suficiente para cubrir esta compra.
                            </Alert>
                        )}
                    </Box>

                    {/* Selector de Acción */}
                    <Box>
                        {needsRecharge ? (
                            // OPCIÓN A: RECARGAR Y PAGAR (Tu componente MultiOrder)
                            <Box>
                                <Typography variant="h6" gutterBottom fontWeight="bold">Recargar Diferencia</Typography>
                                <MultiOrderPaymentContainer 
                                    paymentData={{
                                        total_to_pay: missingAmount,
                                        orders: createdOrderData.orders // Aquí van los objetos con 'id'
                                    }}
                                    merchantUuid="QUD" 
                                    customer={{
                                        nombre: userData.entity.name,
                                        apellido: userData.entity.last_name,
                                        documento_tipo: userData.entity.document_type,
                                        documento: userData.entity.document_number,
                                        correo:userData.user.email,
                                        prefijo:userData.user.prefijo,
                                        telefono: userData.entity.phone,
                                        profesion: userData.entity.profession
                                    }}
                                    onPaymentSuccess={handleRechargeSuccess}
                                />
                            </Box>
                        ) : (
                            // OPCIÓN B: PAGAR CON SALDO DIRECTO
                            <Box textAlign="center" mt={2}>
                                <Button 
                                    variant="contained" 
                                    size="large"
                                    onClick={handlePayWithWallet}
                                    disabled={processing}
                                    sx={{ 
                                        borderRadius: 50, px: 6, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold',
                                        boxShadow: '0 10px 25px rgba(0,118,255,0.3)'
                                    }}
                                >
                                    {processing ? 'Procesando...' : 'Pagar con mi Saldo'}
                                </Button>
                                <Typography variant="caption" display="block" mt={2} color="text.secondary">
                                    El monto se descontará de tu wallet inmediatamente.
                                </Typography>
                            </Box>
                        )}
                    </Box>

                </Paper>
            </Container>
        );
    }

    // === VISTA A: FORMULARIO INICIAL ===
    const summary = cartData?.summary || { subtotal: 0, total: 0 };

    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
            <CustomBreadcrumbs returnTo='/cart' crumbs={[{ name: 'Carrito', link: '/cart' }, { name: 'Checkout', link: '/checkout' }]} />
            
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 2, mb: 4 }}>
                Confirmar Envío
            </Typography>

            <Grid container spacing={4}>
                
                {/* IZQUIERDA: DATOS DE ENVÍO */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                        <Box display="flex" alignItems="center" gap={2} mb={3}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                            </Avatar>
                            <Typography variant="h6" fontWeight="bold">Dirección de Entrega</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth required label="Dirección Exacta" name="address"
                                    value={formData.address} onChange={handleInputChange}
                                    placeholder="Calle, Número, Edificio..."
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Ciudad" name="city" value={formData.city} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Zona / Zip" name="zip" value={formData.zip} onChange={handleInputChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth multiline rows={2} label="Notas (Opcional)" name="notes" value={formData.notes} onChange={handleInputChange} />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* DERECHA: RESUMEN SIMPLE */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 3, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 3, position: 'sticky', top: 100 }}>
                        <Typography variant="h6" fontWeight="bold" mb={3}>Resumen</Typography>

                        <Box display="flex" justifyContent="space-between" mb={2}>
                            <Typography color="text.secondary">Ítems</Typography>
                            <Typography fontWeight="bold">{cartData.items.length}</Typography>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" mb={3} alignItems="flex-end">
                            <Typography variant="h6">Total a Pagar</Typography>
                            <Typography variant="h4" fontWeight="bold" color="primary.main">
                                ${summary.total}
                            </Typography>
                        </Box>

                        <Button 
                            fullWidth 
                            variant="contained" 
                            size="large"
                            onClick={handleGenerateOrders}
                            disabled={processing}
                            endIcon={!processing && <FontAwesomeIcon icon={faArrowRight} />}
                            sx={{ borderRadius: 50, py: 1.5, fontWeight: 'bold' }}
                        >
                            {processing ? 'Generando...' : 'Confirmar Datos'}
                        </Button>
                        
                        <Typography variant="caption" display="block" textAlign="center" mt={2} color="text.secondary">
                            En el siguiente paso podrás usar tu saldo o recargar si es necesario.
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <QUDProgress open={processing} />
            <ResponseComponent 
                show={feedback.show} success={feedback.success} title={feedback.title} message={feedback.message} 
                onClick={() => setFeedback({ ...feedback, show: false })}
            />
        </Container>
    );
}